import React from "react";

import { useNavigation, useNotification } from "@refinedev/core";

import { MergeButton } from "@/components/common/merge-button";
import { RemoteSelect } from "@/components/fields/remote-select";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import { ArtistNestedType } from "@/pages/artists/schemas/artist.nested.form";

/**
 * Merge this artist into another one, then jump to the target.
 *
 * Unlike the venue equivalent this is NOT gated on the follower count. Moving
 * followers is the whole point of an artist merge: duplicates are created by the
 * ingestion pipelines and both copies accumulate followers before a curator
 * notices.
 */
export const ArtistMergeButton = ({ id }: { id: string }) => {
  const { search, merge } = useArtistRepository();
  const { open } = useNotification();
  const { edit } = useNavigation();
  const [value, setValue] = React.useState<ArtistNestedType>();
  const [loading, setLoading] = React.useState(false);

  return (
    <MergeButton
      loading={loading}
      resource="artists"
      description={
        <>
          Followers, events and notification subscriptions move to the artist you
          pick. This one is then hidden and kept as a redirect, so its links stay
          alive. You can undo this from the hidden artist's page.
        </>
      }
      onConfirm={async (close) => {
        if (!value?.id) {
          open?.({
            type: "error",
            message: "Please select an artist to merge into",
          });
          return;
        }
        // `value.id` is nullish on ArtistNestedType because the same schema backs
        // lineup entries typed before an artist exists; a search hit always has one.
        const targetId = value.id;
        open?.({
          type: "success",
          message: `Merging artist ${id} into ${value.name}...`,
        });
        setLoading(true);
        await merge(id, targetId).finally(() => setLoading(false));
        edit("artists", targetId);
        close();
      }}
    >
      <RemoteSelect<ArtistNestedType>
        value={value}
        onSearch={async (query) => {
          const response = await search(query);
          return response.filter((d) => d.id !== id);
        }}
        onValueChanged={(value) => {
          if (value) setValue(value);
        }}
        showSelected
        accessorFn={(option) => ({
          id: option?.id || "",
          // Genre and follower count, not address: two artists of the same name are
          // told apart by what they play and how big they are.
          name: option
            ? [
                option.name,
                option.genres?.[0]?.name,
                `${option.followers ?? 0} followers`,
              ]
                .filter(Boolean)
                .join(" · ")
            : "",
          iconUrl: option?.media?.photos?.[0]?.url ?? option?.photo?.url,
        })}
      />
    </MergeButton>
  );
};
